import { customElement, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { IRouterEvents } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './app.html';
import { THEME_CHANGED_CHANNEL } from './views/settings';
import { DocumentsList } from './views/documents-list';
import { SettingsView } from './views/settings';
import { TagsList } from './views/tags-list';
import { RulesList } from './views/rules-list';
import { FormsList } from './views/forms-list';
import { FollowUpsView } from './views/follow-ups-view';
import { IncomingView, INCOMING_COUNT_CHANNEL } from './views/incoming-view';
import { StatisticsView } from './views/statistics-view';
import { MaintenanceView } from './views/maintenance-view';
import { HelpView } from './views/help-view';
import { BookmarksView } from './views/bookmarks-view';
import { upsertUserSetting, getIncomingCount, getMe, getUserSettings } from './services/api.service';
import { PERMISSIONS_CHANGED_CHANNEL, setPermissions } from './services/permissions.service';
import { startSse, onSseEvent, SSE_CONNECTED } from './services/sse.service';
import { LANGUAGE_CHANGED_CHANNEL, LANGUAGES } from './services/languages';
import { setDateFormat, type DateFormatOption } from './services/date-format.service';
import { apiUrl } from './services/runtime-config';
import { applicationDisplayVersion } from './services/application-version';
import { ImportProtocolsList } from './views/import-protocols-list';
import { ImportProtocolFilesView } from './views/import-protocol-files-view';

@route({
  routes: [
    { path: '', redirectTo: 'followups' },
    { path: 'incoming', component: IncomingView, title: 'Incoming' },
    { path: 'documents', component: DocumentsList, title: 'Documents' },
    { path: 'statistics', component: StatisticsView, title: 'Statistics' },
    { path: 'maintenance', component: MaintenanceView, title: 'Maintenance' },
    { path: 'settings', component: SettingsView, title: 'Settings' },
    { path: 'help', component: HelpView, title: 'Help' },
    { path: 'bookmarks', component: BookmarksView, title: 'Bookmarks' },
    { path: 'tags', component: TagsList, title: 'Tags' },
    { path: 'rules', component: RulesList, title: 'Rules' },
    { path: 'forms', component: FormsList, title: 'Forms' },
    { path: 'followups', component: FollowUpsView, title: 'Follow-ups' },
    { path: 'import-protocols', component: ImportProtocolsList, title: 'Import protocols' },
    { path: 'import-protocol-files', component: ImportProtocolFilesView, title: 'Imported files' },
  ],
})
@customElement({ name: 'app', template })
export class App {
  private readonly i18n: I18N = resolve(I18N);
  private readonly routerEvents: IRouterEvents = resolve(IRouterEvents);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _navSub: IDisposable | null = null;
  private _localeSub: IDisposable | null = null;
  private _themeSub: IDisposable | null = null;

  public sidebarCollapsed = false;
  public language = localStorage.getItem('radish-language') ?? 'en';
  public theme: 'light' | 'dark' = (localStorage.getItem('radish-theme') as 'light' | 'dark') ?? 'light';
  public showLangMenu = false;
  public showUserMenu = false;
  public currentViewKey = this._pathToKey(window.location.pathname);
  public incomingCount = 0;
  public currentUsername = '';
  public currentDisplayName = '';
  /** Permission keys granted to the current user (from /me). Bound directly in
   *  templates via `permissions.includes('...')` so Aurelia's observer picks up
   *  changes when this array is reassigned after /me resolves. Calling a plain
   *  imported function here would NOT be reactive, since Aurelia can't observe
   *  reads happening inside an opaque method body. */
  public permissions: string[] = [];
  /** True once auth check passes and the app is ready to render. */
  public isReady = false;
  public readonly applicationDisplayVersion = applicationDisplayVersion;
  private _sseSub?: () => void;
  private _sseConnectedSub?: () => void;
  private _incomingCountSub?: IDisposable;

  public readonly languages = LANGUAGES;

  public get currentCountryCode(): string {
    return this.languages.find(l => l.value === this.language)?.countryCode ?? 'us';
  }

  public async binding(): Promise<void> {
    // --- Auth guard: verify session before rendering any UI ---
    try {
      const statusRes = await fetch(apiUrl('/auth/status'));
      const statusData = await statusRes.json() as { authenticated: boolean };
      if (!statusData.authenticated) {
        window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
        return;
      }
    } catch {
      window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
      return;
    }

    this.isReady = true;

    this._navSub = this.routerEvents.subscribe('au:router:navigation-end', () => {
      this.currentViewKey = this._pathToKey(window.location.pathname);
      // Resync the badge whenever the user (re)enters the Incoming view, so a
      // missed SSE announcement can't leave it stale indefinitely.
      if (this.currentViewKey === 'incoming') {
        void getIncomingCount().then(r => { this.incomingCount = r.count; }).catch(() => {/* ignore */});
      }
    });

    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this.language = this.i18n.getLocale();
    });

    // Sync theme when changed externally (e.g. settings page)
    this._themeSub = this.ea.subscribe(THEME_CHANGED_CHANNEL, (theme: 'light' | 'dark') => {
      this.theme = theme;
    });

    // Start SSE and subscribe to incoming_count_changed
    startSse();
    this._sseSub = onSseEvent('incoming_count_changed', (payload) => {
      const p = payload as { count: number };
      this.incomingCount = p.count ?? 0;
    });
    // Re-fetch the authoritative count whenever the stream (re)connects, since any
    // change events that were published while disconnected would otherwise be lost.
    this._sseConnectedSub = onSseEvent(SSE_CONNECTED, () => {
      void getIncomingCount().then(r => { this.incomingCount = r.count; }).catch(() => {/* ignore */});
    });
    // The Incoming view itself always knows the true count after it loads its list
    // (or after it deletes documents locally); let it push that value directly so
    // the badge can't drift even if the server-sent event was missed entirely.
    this._incomingCountSub = this.ea.subscribe(INCOMING_COUNT_CHANNEL, (count: number) => {
      this.incomingCount = count;
    });

    // Load initial incoming count and current user, then apply persisted user settings.
    // Settings are loaded from the API (keyed by username) so switching users
    // immediately applies the correct theme/language/dateFormat.
    void getIncomingCount().then(r => { this.incomingCount = r.count; }).catch(() => {/* ignore */});
    void getMe()
      .then(r => {
        this.currentUsername = r.username;
        this.currentDisplayName = r.displayName ?? r.username;
        this.permissions = r.permissions ?? [];
        setPermissions(this.permissions);
        this.ea.publish(PERMISSIONS_CHANGED_CHANNEL, this.permissions);
        return getUserSettings();
      })
      .then(settings => {
        for (const s of settings) {
          if (s.key === 'theme') {
            const t = s.value as 'light' | 'dark';
            this.theme = t;
            document.documentElement.setAttribute('data-theme', t);
            localStorage.setItem('radish-theme', t);
            this.ea.publish(THEME_CHANGED_CHANNEL, t);
          }
          if (s.key === 'language') {
            this.language = s.value;
            void this.i18n.setLocale(s.value);
            localStorage.setItem('radish-language', s.value);
            this.ea.publish(LANGUAGE_CHANGED_CHANNEL, s.value);
          }
          if (s.key === 'dateFormat') {
            setDateFormat(s.value as DateFormatOption);
          }
        }
      })
      .catch(() => {/* not logged in or settings unavailable */});
  }

  public unbinding(): void {
    this._navSub?.dispose();
    this._localeSub?.dispose();
    this._themeSub?.dispose();
    this._sseSub?.();
    this._sseConnectedSub?.();
    this._incomingCountSub?.dispose();
  }

  public toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  public async toggleTheme(): Promise<void> {
    const next: 'light' | 'dark' = this.theme === 'light' ? 'dark' : 'light';
    this.theme = next;
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('radish-theme', next);
    this.ea.publish(THEME_CHANGED_CHANNEL, next);
    await upsertUserSetting('theme', next).catch(() => {/* ignore */});
  }

  public toggleLangMenu(): void {
    this.showLangMenu = !this.showLangMenu;
  }

  public closeLangMenuDelayed(): void {
    setTimeout(() => { this.showLangMenu = false; }, 150);
  }

  public toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  public closeUserMenuDelayed(): void {
    setTimeout(() => { this.showUserMenu = false; }, 150);
  }

  public logout(): void {
    window.location.href = apiUrl('/auth/logout');
  }

  public async changeLanguage(lang: string): Promise<void> {
    this.language = lang;
    this.showLangMenu = false;
    void this.i18n.setLocale(lang);
    localStorage.setItem('radish-language', lang);
    this.ea.publish(LANGUAGE_CHANGED_CHANNEL, lang);
    await upsertUserSetting('language', lang).catch(() => {/* ignore */});
  }

  private _pathToKey(path: string): string {
    const segment = path.replace(/^\//, '').split('/')[0] || 'followups';
    const map: Record<string, string> = {
      incoming:  'nav.incoming',
      documents: 'nav.documents',
      statistics: 'nav.statistics',
      maintenance: 'nav.maintenance',
      settings:  'nav.settings',
      help:      'nav.help',
      bookmarks: 'nav.bookmarks',
      tags:      'nav.tags',
      rules:     'nav.rules',
      forms:     'nav.forms',
      followups: 'nav.followUps',
      'import-protocols': 'nav.importProtocols',
      'import-protocol-files': 'nav.importProtocols',
    };
    return map[segment] ?? 'nav.followUps';
  }
}
